import React, { useState, useEffect } from 'react';
import { History, Plus, Trash2, FileText, Loader2, Search, MessageSquare, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { api } from '../../api';
import { useAuth } from '../Auth/AuthContext';

const STATUS_LABEL = {
  draft: { label: 'Utkast', cls: 'bg-slate-100 text-slate-600' },
  sent: { label: 'Skickad', cls: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Accepterad', cls: 'bg-green-100 text-green-700' },
  invoiced: { label: 'Fakturerad', cls: 'bg-purple-100 text-purple-700' },
  declined: { label: 'Avböjd', cls: 'bg-red-100 text-red-600' },
};

export default function QuotesListPage({ onOpenQuote, onNewQuote }) {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { loadQuotes(); }, []);

  const loadQuotes = async () => {
    setLoading(true);
    try {
      const data = await api.getQuotes();
      setQuotes(data);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Ta bort offerten?')) return;
    try {
      await api.deleteQuote(id);
      setQuotes(prev => prev.filter(q => q.id !== id));
    } catch (e) { alert(e.message); }
  };

  const handleStatusChange = async (id, status, e) => {
    e.stopPropagation();
    try {
      await api.updateQuoteStatus(id, status);
      setQuotes(prev => prev.map(q => q.id === id ? { ...q, status } : q));
    } catch (e) { alert(e.message); }
  };

  const fmt = (n) => Math.round(n || 0).toLocaleString('sv-SE') + ' kr';

  const revenue = {
    potential: quotes.filter(q => ['draft','sent'].includes(q.status)).reduce((s, q) => s + parseFloat(q.total || 0), 0),
    potentialCount: quotes.filter(q => ['draft','sent'].includes(q.status)).length,
    incoming: quotes.filter(q => q.status === 'accepted').reduce((s, q) => s + parseFloat(q.total || 0), 0),
    incomingCount: quotes.filter(q => q.status === 'accepted').length,
    invoiced: quotes.filter(q => q.status === 'invoiced').reduce((s, q) => s + parseFloat(q.total || 0), 0),
    invoicedCount: quotes.filter(q => q.status === 'invoiced').length,
  };

  const filtered = quotes.filter(q =>
    (q.customer_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (q.quote_number || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-black flex items-center gap-3">
          <History className="text-blue-600" /> Offerter
        </h2>
        <button
          onClick={onNewQuote}
          className="bg-blue-600 text-white px-4 py-2 rounded-xl font-bold flex items-center gap-2 shadow-lg"
        >
          <Plus size={18} /> Ny offert
        </button>
      </div>

      {/* Intäktsöversikt */}
      {quotes.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Potentiellt', sub: 'Utkast & skickade', value: revenue.potential, count: revenue.potentialCount, icon: TrendingUp, color: 'text-blue-600 bg-blue-50 border-blue-100' },
            { label: 'Kommande', sub: 'Accepterade', value: revenue.incoming, count: revenue.incomingCount, icon: Clock, color: 'text-green-700 bg-green-50 border-green-100' },
            { label: 'Fakturerat', sub: 'Fakturerade', value: revenue.invoiced, count: revenue.invoicedCount, icon: CheckCircle, color: 'text-purple-700 bg-purple-50 border-purple-100' },
          ].map((s, i) => (
            <div key={i} className={`rounded-2xl border p-4 ${s.color}`}>
              <div className="flex items-center gap-2 mb-2">
                <s.icon size={14} />
                <span className="text-[10px] font-black uppercase tracking-wider">{s.label}</span>
              </div>
              <p className="text-xl font-black leading-none mb-1">{fmt(s.value)}</p>
              <p className="text-[11px] opacity-60">{s.count} offerter · {s.sub}</p>
            </div>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-3.5 text-slate-400" size={18} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl bg-white outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Sök på kund eller offertnummer..."
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-600" size={32} /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
          <FileText className="mx-auto text-slate-300 mb-4" size={48} />
          <p className="text-slate-500 font-bold">{search ? 'Inga offerter hittades' : 'Inga offerter ännu'}</p>
          {!search && (
            <button onClick={onNewQuote} className="mt-4 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold">
              Skapa din första offert
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(quote => {
            const status = STATUS_LABEL[quote.status] || STATUS_LABEL.draft;
            return (
              <div
                key={quote.id}
                onClick={() => onOpenQuote(quote)}
                className="bg-white border border-slate-200 rounded-2xl p-5 cursor-pointer hover:border-blue-400 hover:shadow-md transition-all group"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className="font-black text-lg truncate">{quote.customer_name || 'Ingen kund'}</h3>
                      <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-full flex-shrink-0 ${status.cls}`}>
                        {status.label}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 font-bold uppercase mb-1">
                      {quote.quote_number ? `Nr: ${quote.quote_number}` : 'Inget nummer'}
                    </p>
                    <p className="text-xs text-slate-400">
                      {new Date(quote.updated_at).toLocaleDateString('sv-SE')} · Skapad av {quote.created_by_name || quote.created_by_email}
                    </p>
                    {quote.customer_response && quote.customer_response !== 'accepted' && quote.customer_response !== 'declined' && (
                      <div className="flex items-start gap-1.5 mt-2 bg-slate-50 rounded-xl px-3 py-2 border border-slate-100">
                        <MessageSquare size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-slate-500 italic">"{quote.customer_response}"</p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-2xl font-black text-blue-600">{Math.round(quote.total).toLocaleString()} kr</p>
                    </div>
                  </div>
                </div>

                {/* Status-knappar */}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100 flex-wrap">
                  {Object.entries(STATUS_LABEL).map(([key, val]) => (
                    <button
                      key={key}
                      onClick={(e) => handleStatusChange(quote.id, key, e)}
                      className={`text-[10px] font-black uppercase px-2 py-1 rounded-full transition-all
                        ${quote.status === key ? val.cls : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`}
                    >
                      {val.label}
                    </button>
                  ))}
                  {user?.role === 'admin' && (
                    <button
                      onClick={(e) => handleDelete(quote.id, e)}
                      className="ml-auto text-slate-300 hover:text-red-500 p-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
